/**
 * Custom hook for managing qualifiers
 * Provides CRUD operations and state management for qualifiers
 */

import { useState, useEffect, useCallback } from 'react';
import qualifierService from '../services/qualifierService';
import { useAlert } from '../context/AlertContext';
import { useAccessToken } from './useAuth';

export const useQualifiers = (filterType = 'all') => {
    const { getBackendAccessToken } = useAccessToken();
    const { setInfoAlert, setErrorAlert } = useAlert();
    const [qualifiers, setQualifiers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Fetch qualifiers based on filter type
     */
    const fetchQualifiers = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const accessToken = await getBackendAccessToken();

            let data;
            if (filterType === 'active') {
                data = await qualifierService.getActive(accessToken);
            } else if (filterType === 'inactive') {
                data = await qualifierService.getInactive(accessToken);
            } else {
                data = await qualifierService.getAll(accessToken);
            }

            setQualifiers(data);
        } catch (err) {
            console.error('Error fetching qualifiers:', err);
            setError(err.message || 'Failed to load qualifiers');
            setErrorAlert('Failed to load qualifiers');
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, filterType, setErrorAlert]);

    /**
     * Create a new qualifier
     */
    const createQualifier = async (qualifierData) => {
        try {
            const accessToken = await getBackendAccessToken();

            const newQualifier = await qualifierService.create(accessToken, qualifierData);
            setInfoAlert('Qualifier created successfully');
            await fetchQualifiers(); // Refresh the list
            return newQualifier;
        } catch (err) {
            console.error('Error creating qualifier:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Failed to create qualifier';
            setErrorAlert(errorMessage);
            throw err;
        }
    };

    /**
     * Update an existing qualifier
     */
    const updateQualifier = async (id, qualifierData) => {
        try {
            const accessToken = await getBackendAccessToken();

            const updatedQualifier = await qualifierService.update(accessToken, id, qualifierData);
            setInfoAlert('Qualifier updated successfully');
            await fetchQualifiers(); // Refresh the list
            return updatedQualifier;
        } catch (err) {
            console.error('Error updating qualifier:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Failed to update qualifier';
            setErrorAlert(errorMessage);
            throw err;
        }
    };

    /**
     * Delete a qualifier
     */
    const deleteQualifier = async (id) => {
        try {
            const accessToken = await getBackendAccessToken();

            await qualifierService.delete(accessToken, id);
            setInfoAlert('Qualifier deleted successfully');
            await fetchQualifiers(); // Refresh the list
        } catch (err) {
            console.error('Error deleting qualifier:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Failed to delete qualifier';
            setErrorAlert(errorMessage);
            throw err;
        }
    };

    /**
     * Toggle qualifier active status
     */
    const toggleQualifierActive = async (qualifier) => {
        try {
            const accessToken = await getBackendAccessToken();

            if (qualifier.active) {
                await qualifierService.deactivate(accessToken, qualifier.qualifierId);
            } else {
                await qualifierService.activate(accessToken, qualifier.qualifierId);
            }
            
            setInfoAlert(`Qualifier ${qualifier.active ? 'deactivated' : 'activated'} successfully`);
            await fetchQualifiers(); // Refresh the list
        } catch (err) {
            console.error('Error toggling qualifier active status:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Failed to toggle qualifier status';
            setErrorAlert(errorMessage);
            throw err;
        }
    };

    // Fetch qualifiers on mount and when filter changes
    useEffect(() => {
        fetchQualifiers();
    }, [fetchQualifiers]);

    return {
        qualifiers,
        loading,
        error,
        refresh: fetchQualifiers,
        createQualifier,
        updateQualifier,
        deleteQualifier,
        toggleQualifierActive
    };
};
